import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["referenceIndicator", "referenceText", "referenceSize", "referenceTabIndex", "referenceStyles", "placement", "disabled", "menuInlineSize", "menuMaxBlockSize", "closeOnMenuClick", "open", "autoUpdateMenuPosition", "data-test-id", "onActionClick", "onOpenChange", "use", "shape", "actions"],
  _excluded2 = ["aria-label", "aria-labelledby"];
/* eslint-disable @typescript-eslint/no-shadow */
import { useCallback, useRef, useState } from 'react';
import { useFloating, useListNavigation, useTypeahead, useClick, useDismiss, useRole, useInteractions, offset, flip, size, shift, hide, autoUpdate, FloatingPortal, FloatingFocusManager } from '@floating-ui/react';
import unescapedText from 'I18n/utils/unescapedText';
import { DownCaratIcon } from 'foundations-assets';
import { Flex } from '../Flex/Flex';
import { Text } from '../Text/Text';
import { useControlledOpen } from '../../hooks/useControlledOpen';
import { getFlattenedActions, getReferenceLayout } from './ActionsMenu.helpers';
import { StyledContainer, StyledReferenceIndicator, StyledReferenceText, StyledReferenceWrapper, StyledDownCaratIconWrapper, StyledMenu, StyledMenuOptionsWrapper, StyledMenuOption, StyledMenuOptionContentWrapper, StyledMenuOptionIndicator } from './ActionsMenu.styles';
import { Button } from '../../transitional/components/Button';
import { IconButton } from '../../transitional/components/IconButton';
import { filterProps } from '../../utils/filterProps';
import { useActionsCollapserContext } from '../../transitional/components/ActionsCollapser/ActionsCollapserProvider';
import { useTabsContext } from '../../transitional/components/Tabs/TabsProvider';
import { useMapTokenPropsToCSS } from '../../hooks/useMapTokenPropsToCSS';
import { mergeOverrides } from '../../utils/mergeOverrides';
import { getDefaultNode } from '../../transitional/utils/RootNode';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const ActionsMenu = _ref => {
  var _actionsCollapserCont, _tabsContext$actionsM, _middlewareData$hide;
  let {
      referenceIndicator,
      referenceText,
      referenceSize = 'default',
      referenceTabIndex,
      referenceStyles,
      placement = 'bottom-start',
      disabled,
      menuInlineSize,
      menuMaxBlockSize = 285,
      closeOnMenuClick = true,
      open: controlledOpen,
      autoUpdateMenuPosition = true,
      'data-test-id': dataTestId,
      onActionClick,
      onOpenChange,
      use,
      shape,
      actions: actionsProp
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    isOpen,
    setIsOpen
  } = useControlledOpen({
    controlledOpen,
    onOpenChange
  });
  const [activeIndex, setActiveIndex] = useState(null);
  const listRef = useRef([]);
  const listContentRef = useRef([]);
  const isTypingRef = useRef(false);
  const referenceLayout = getReferenceLayout(referenceIndicator, referenceText);
  const actions = getFlattenedActions(actionsProp);
  const {
    refs,
    context,
    floatingStyles,
    middlewareData
  } = useFloating({
    placement,
    open: isOpen,
    onOpenChange: setIsOpen,
    whileElementsMounted: autoUpdateMenuPosition ? autoUpdate : undefined,
    middleware: [offset(5), flip({
      padding: 10
    }), shift({
      padding: 10
    }), size({
      apply({
        rects,
        elements
      }) {
        let inlineSize = `${menuInlineSize !== null && menuInlineSize !== void 0 ? menuInlineSize : rects.reference.width}px`;
        if (referenceLayout === 'indicator-only') {
          inlineSize = `${menuInlineSize !== null && menuInlineSize !== void 0 ? menuInlineSize : rects.floating.width}px`;
        }
        Object.assign(elements.floating.style, {
          maxBlockSize: `${menuMaxBlockSize}px`,
          inlineSize
        });
      },
      padding: 10
    }), hide()]
  });
  const click = useClick(context, {
    event: 'mousedown'
  });
  const dismiss = useDismiss(context);
  const role = useRole(context, {
    role: 'listbox'
  });
  const listNav = useListNavigation(context, {
    listRef,
    activeIndex,
    onNavigate: setActiveIndex,
    loop: true,
    virtual: true,
    focusItemOnOpen: true,
    focusItemOnHover: false
  });
  const typeahead = useTypeahead(context, {
    listRef: listContentRef,
    activeIndex,
    onMatch: setActiveIndex,
    enabled: true,
    onTypingChange(isTyping) {
      isTypingRef.current = isTyping;
    }
  });
  const {
    getReferenceProps,
    getFloatingProps,
    getItemProps
  } = useInteractions([dismiss, role, listNav, typeahead, click]);
  const handleActionClick = useCallback(value => {
    onActionClick({
      value
    });
    if (closeOnMenuClick) {
      setIsOpen(false);
    }
  }, [closeOnMenuClick, onActionClick, setIsOpen]);
  const handleOptionKeyDown = useCallback(event => {
    if (disabled) return;
    if (event.key === 'Enter' || event.key === ' ' && !isTypingRef.current) {
      event.preventDefault();
      const activeOption = actions[activeIndex];
      if (activeOption) {
        handleActionClick(activeOption.value);
      }
    }
  }, [disabled, actions, activeIndex, handleActionClick]);
  const ReferenceButton = referenceLayout === 'indicator-only' ? IconButton : Button;
  const {
    domProps
  } = filterProps(rest);
  const {
    tokenProps
  } = filterProps(referenceStyles || {});
  const {
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy
    } = domProps,
    otherDomProps = _objectWithoutPropertiesLoose(domProps, _excluded2);
  const marginStyles = useMapTokenPropsToCSS(tokenProps);
  const actionsCollapserContext = useActionsCollapserContext();
  const tabsContext = useTabsContext();
  const sharedStyleOverrides = mergeOverrides((_actionsCollapserCont = actionsCollapserContext === null || actionsCollapserContext === void 0 ? void 0 : actionsCollapserContext.actionsMenuStyleOverrides) !== null && _actionsCollapserCont !== void 0 ? _actionsCollapserCont : {}, (_tabsContext$actionsM = tabsContext === null || tabsContext === void 0 ? void 0 : tabsContext.actionsMenuStyleOverrides) !== null && _tabsContext$actionsM !== void 0 ? _tabsContext$actionsM : {});
  return /*#__PURE__*/_jsxs(StyledContainer, Object.assign({}, otherDomProps, {
    $styleOverrides: sharedStyleOverrides,
    children: [/*#__PURE__*/_jsx(StyledReferenceWrapper, {
      $referenceSize: referenceSize,
      $referenceLayout: referenceLayout,
      $marginStyles: marginStyles,
      children: /*#__PURE__*/_jsx(ReferenceButton, Object.assign({
        buttonRef: refs.setReference,
        "aria-label": ariaLabel || referenceText || unescapedText('foundations-components.ActionsMenu.defaultAriaLabel'),
        "aria-labelledby": ariaLabelledBy,
        "data-test-id": dataTestId,
        disabled: disabled,
        use: use,
        size: referenceSize === 'default' ? 'md' : 'sm'
      }, referenceLayout === 'indicator-only' && {
        shape
      }, getReferenceProps({
        onClick: e => e.stopPropagation()
      }), referenceTabIndex !== undefined ? {
        tabIndex: referenceTabIndex
      } : {}, {
        children: /*#__PURE__*/_jsxs(Flex, {
          alignItems: "center",
          justifyContent: "center",
          inlineSize: "100%",
          children: [/*#__PURE__*/_jsxs(Flex, {
            alignItems: "center",
            overflow: "hidden",
            gap: referenceLayout === 'indicator-text' ? '200' : '0',
            children: [referenceIndicator && /*#__PURE__*/_jsx(StyledReferenceIndicator, {
              $referenceSize: referenceSize,
              children: referenceIndicator
            }), referenceText && /*#__PURE__*/_jsx(StyledReferenceText, {
              children: referenceText
            })]
          }), referenceLayout !== 'indicator-only' && /*#__PURE__*/_jsx(StyledDownCaratIconWrapper, {
            $iconSize: "100",
            children: /*#__PURE__*/_jsx(DownCaratIcon, {})
          })]
        })
      }))
    }), isOpen && !disabled && /*#__PURE__*/_jsx(FloatingPortal, {
      root: getDefaultNode(),
      children: /*#__PURE__*/_jsx(FloatingFocusManager, {
        context: context,
        modal: false,
        returnFocus: true,
        children: /*#__PURE__*/_jsx(StyledMenu, Object.assign({
          $isReferenceHidden: (_middlewareData$hide = middlewareData.hide) === null || _middlewareData$hide === void 0 ? void 0 : _middlewareData$hide.referenceHidden,
          ref: refs.setFloating,
          style: floatingStyles
        }, getFloatingProps(), {
          "data-tinymce-floating-element": "true",
          children: /*#__PURE__*/_jsx(StyledMenuOptionsWrapper, {
            children: actions.map(({
              text,
              value,
              disabled,
              indicator,
              divider,
              isSubheading
            }, index) => {
              const isValidAction = !disabled && !isSubheading;
              const isActive = activeIndex === index;
              const color = disabled ? 'text-primary-disabled' : 'text-core-default';
              return /*#__PURE__*/_jsx(StyledMenuOption, Object.assign({
                $isActive: isActive,
                $disabled: disabled,
                $divider: divider,
                $isSubheading: isSubheading,
                "data-text": text,
                "data-value": value,
                ref: node => {
                  if (isValidAction) {
                    listRef.current[index] = node;
                    listContentRef.current[index] = text;
                  }
                },
                role: "option",
                tabIndex: isActive ? 0 : -1,
                "aria-disabled": disabled
              }, getItemProps({
                onClick(e) {
                  e.stopPropagation();
                  if (isValidAction) handleActionClick(value);
                },
                onKeyDown(event) {
                  handleOptionKeyDown(event);
                },
                onPointerEnter() {
                  if (isValidAction) {
                    setActiveIndex(index);
                  }
                },
                onPointerLeave() {
                  setActiveIndex(null);
                }
              }), {
                children: /*#__PURE__*/_jsxs(StyledMenuOptionContentWrapper, {
                  $optionTextColor: color,
                  $hasIndicator: Boolean(indicator),
                  type: "button",
                  children: [indicator && /*#__PURE__*/_jsx(StyledMenuOptionIndicator, {
                    children: indicator
                  }), /*#__PURE__*/_jsx(Text, {
                    truncate: true,
                    color: color,
                    typography: isSubheading ? 'heading-50' : undefined,
                    children: text
                  })]
                })
              }), value);
            })
          })
        }))
      })
    })]
  }));
};